import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogTitle, Divider,
    FormControlLabel, IconButton, MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ActionReferenceField from "./ActionReferenceField";
import { GeneralButton } from "../../common/ui/GeneralButton";
import { useRef, useState } from "react";
import {
    GenericInAppNotification,
    InAppNotificationAction,
    InAppNotificationActionType,
    InAppNotificationImage
} from "../../types/notificationType";
import TranslatableTextField from "../../components/common/translations/TranslatableTextField";
import SelectGroupsList from "../../components/groups/SelectGroupsList";
import SelectEmployeesList from "../../components/employees/SelectEmployeesList";
import CloseIcon from "@mui/icons-material/Close";
import { notificationApi } from "../../api/notificationApi";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { handleRequest } from "../../store/utils/handleRequest";

interface Props {
    onSave: (notification: GenericInAppNotification) => Promise<number | undefined>;
    onDelete?: (notification: GenericInAppNotification) => Promise<void>;
    onClose: () => void;
    onRefresh?: () => void;

    editingNotification?: GenericInAppNotification;
}

interface PendingImage {
    file: File;
    previewUrl: string;
}

export default function CreateOrEditGenericNotificationDialog(props: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [formData, setFormData] = useState<GenericInAppNotification>(props.editingNotification ?? {
        message: "",
        title: "",
        isCompanyWideAssigned: false,
        assignedEmployeeUserIds: [],
        assignedGroupIds: [],
        sendPushNotification: false,
        images: [],
    });

    const [existingImages, setExistingImages] = useState<InAppNotificationImage[]>(formData.images ?? []);
    const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
    const [imagesMarkedForDeletion, setImagesMarkedForDeletion] = useState<number[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setPendingImages(prev => [...prev, { file: selected, previewUrl: URL.createObjectURL(selected) }]);
        e.target.value = "";
    };

    const handleRemoveExistingImage = (imageId: number) => {
        setExistingImages(prev => prev.filter(img => img.id !== imageId));
        setImagesMarkedForDeletion(prev => [...prev, imageId]);
    };

    const handleRemovePendingImage = (previewUrl: string) => {
        setPendingImages(prev => prev.filter(img => img.previewUrl !== previewUrl));
        URL.revokeObjectURL(previewUrl);
    };

    const handleSave = async () => {
        if (!isActionValid(formData.action)) {
            setSubmitAttempted(true);
            return;
        }
        setIsSaving(true);
        try {
            const savedNotificationId = await props.onSave(formData);
            if (!savedNotificationId) return;

            let hadError = false;

            for (const pending of pendingImages) {
                const result = await handleRequest(
                    dispatch,
                    "direct",
                    () => notificationApi.uploadNotificationImage(savedNotificationId, pending.file)
                );
                if (!result.success()) {
                    hadError = true;
                } else {
                    setPendingImages(prev => prev.filter(img => img.previewUrl !== pending.previewUrl));
                    URL.revokeObjectURL(pending.previewUrl);
                }
            }

            for (const imageId of imagesMarkedForDeletion) {
                const result = await handleRequest(
                    dispatch,
                    "direct",
                    () => notificationApi.deleteNotificationImage(savedNotificationId, imageId)
                );
                if (!result.success()) {
                    hadError = true;
                } else {
                    setImagesMarkedForDeletion(prev => prev.filter(id => id !== imageId));
                }
            }

            props.onRefresh?.();

            if (!hadError) {
                props.onClose();
            }
        } finally {
            setIsSaving(false);
        }
    };

    return <Dialog open={true} fullWidth maxWidth="md">
        <DialogTitle>
            {props.editingNotification
                ? t("genericInAppNotificationsPage.editTitle")
                : t("genericInAppNotificationsPage.newTitle")}
        </DialogTitle>

        <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 3 }}>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TranslatableTextField
                    label={t("genericInAppNotificationsPage.columns.title")}
                    value={formData.title}
                    onChange={(v) => setFormData({ ...formData, title: v })}
                    entityType="GenericInAppNotification"
                    entityId={String(formData.id)}
                    property="title"
                />
                <TranslatableTextField
                    label={t("genericInAppNotificationsPage.columns.message")}
                    value={formData.message}
                    onChange={(v) => setFormData({ ...formData, message: v })}
                    entityType="GenericInAppNotification"
                    entityId={String(formData.id)}
                    property="message"
                />
            </Box>

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle2">
                    {t("genericInAppNotificationsPage.image")}
                </Typography>

                <input type="file" accept="image/*" ref={fileInputRef} hidden onChange={handleImageSelect} />

                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                    {existingImages.map(img => (
                        <Box key={img.id} sx={{ position: "relative", width: 120, height: 90 }}>
                            <img src={img.downloadUrl ?? img.path} alt={img.filename}
                                 style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} />
                            <IconButton size="small" onClick={() => handleRemoveExistingImage(img.id)}
                                        sx={{ position: "absolute", top: 2, right: 2, backgroundColor: "background.paper" }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    {pendingImages.map(img => (
                        <Box key={img.previewUrl} sx={{ position: "relative", width: 120, height: 90 }}>
                            <img src={img.previewUrl} alt={img.file.name}
                                 style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} />
                            <IconButton size="small" onClick={() => handleRemovePendingImage(img.previewUrl)}
                                        sx={{ position: "absolute", top: 2, right: 2, backgroundColor: "background.paper" }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}

                    <Box
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            width: 120, height: 90, display: "flex", alignItems: "center", justifyContent: "center",
                            border: "1px dashed", borderColor: "divider", borderRadius: 1, cursor: "pointer",
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body2">+ {t("genericInAppNotificationsPage.addImage")}</Typography>
                    </Box>
                </Stack>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle2">
                    {t("genericInAppNotificationsPage.visibility")}
                </Typography>
                <FormControlLabel
                    label={t("serviceModulesPage.companyWideAssigned")}
                    control={
                        <Checkbox
                            checked={formData.isCompanyWideAssigned}
                            onChange={(v) => setFormData({ ...formData, isCompanyWideAssigned: v.target.checked })}
                        />
                    }
                />
                {!formData.isCompanyWideAssigned &&
                    <Box sx={{ display: "flex", gap: 2, ml: 4 }}>
                        <Box sx={{ flex: 1 }}>
                            <SelectGroupsList
                                value={formData.assignedGroupIds}
                                onChange={(v) => setFormData({ ...formData, assignedGroupIds: v })}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SelectEmployeesList
                                value={formData.assignedEmployeeUserIds}
                                onChange={(v) => setFormData({ ...formData, assignedEmployeeUserIds: v })}
                            />
                        </Box>
                    </Box>
                }
            </Box>

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="subtitle2">
                        {t("genericInAppNotificationsPage.action.title")}
                    </Typography>
                    {formData.action && (
                        <IconButton size="small" onClick={() => setFormData({ ...formData, action: undefined })} sx={{ color: "text.disabled" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>

                {formData.action
                    ? <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <TextField
                            select
                            label={t("genericInAppNotificationsPage.action.type")}
                            value={formData.action.type}
                            onChange={(e) => setFormData({
                                ...formData,
                                action: { type: e.target.value as InAppNotificationActionType, actionText: formData.action!.actionText, reference: undefined }
                            })}
                        >
                            {(["ShopModule", "ActionModule", "ServiceModule", "Profile", "Wallet", "ExternalUrl"] as InAppNotificationActionType[]).map(type => (
                                <MenuItem key={type} value={type}>{t(`genericInAppNotificationsPage.action.types.${type}`)}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t("genericInAppNotificationsPage.action.actionText")}
                            value={formData.action.actionText}
                            onChange={(e) => setFormData({ ...formData, action: { ...formData.action!, actionText: e.target.value } })}
                        />
                        <ActionReferenceField
                            action={formData.action}
                            onChange={(action) => setFormData({ ...formData, action })}
                            isCompanyWideAssigned={formData.isCompanyWideAssigned}
                            groupIds={formData.assignedGroupIds}
                            employeeUserIds={formData.assignedEmployeeUserIds}
                        />
                        {submitAttempted && !isActionValid(formData.action) && (
                            <Typography variant="caption" color="error">
                                {t("genericInAppNotificationsPage.action.referenceRequired")}
                            </Typography>
                        )}
                    </Box>
                    : <GeneralButton
                        appVariant="secondary"
                        onClick={() => setFormData({ ...formData, action: { type: "ShopModule", actionText: "", reference: undefined } })}
                    >
                        {t("genericInAppNotificationsPage.action.add")}
                    </GeneralButton>
                }
            </Box>
            <Divider />

            <FormControlLabel
                label={t("genericInAppNotificationsPage.sendPushNotification")}
                control={
                    <Checkbox
                        checked={formData.sendPushNotification}
                        onChange={(v) => setFormData({ ...formData, sendPushNotification: v.target.checked })}
                    />
                }
            />
        </Box>

        <DialogActions>
            {props.onDelete && <GeneralButton
                appVariant="danger"
                onClick={async () => {
                    setIsDeleting(true);
                    await props.onDelete!(formData);
                    setIsDeleting(false);
                }}
                disabled={isSaving || isDeleting}
                loading={isDeleting}>
                {t("buttons.delete")}
            </GeneralButton>}

            <GeneralButton
                appVariant="secondary"
                onClick={props.onClose}
                disabled={isSaving || isDeleting}>
                {t("buttons.cancel")}
            </GeneralButton>

            <GeneralButton
                appVariant="primary"
                onClick={handleSave}
                disabled={isSaving || isDeleting}
                loading={isSaving}
            >
                {t("buttons.save")}
            </GeneralButton>
        </DialogActions>
    </Dialog>
}


function isActionValid(action: InAppNotificationAction | undefined): boolean {
    if (!action) return true;
    if (!action.actionText) return false;

    switch (action.type) {
        case "Profile":
        case "Wallet":
            return true;
        case "ExternalUrl":
        case "ShopModule":
        case "ActionModule":
        case "ServiceModule":
            return !!action.reference;
    }
}